import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import { Competition } from '../types/interfaces';
import config from '../config/environment';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import { useNavigate } from 'react-router-dom';
import './AdminCompetitionModal.css';

type AdminCompetitionMode = 'create' | 'edit';

interface AdminCompetitionModalProps {
  isOpen: boolean;
  mode: AdminCompetitionMode;
  onClose: () => void;
  competitions: Competition[];
  onSuccess: () => void;
}

type Visibility = 'public' | 'private' | 'unlisted' | 'showcase';
type Leaderboard = 'public' | 'private' | 'unlisted' | 'final';
type Status = 'new' | 'open' | 'closed' | 'archived';
type CompetitionMode = 'individual' | 'team';
type Prize = 'empty' | 'money' | 'badge';

interface CreateFormData {
  path_id: string;
  title: string;
  description: string;
  submission: string;
  type: string;
  sponsored_by: string;
  start_date: string;
  end_date: string;
  number_of_submissions: string;
  status: Status;
  visibility: Visibility;
  prize: Prize;
  leaderboard: Leaderboard;
  competition_mode: CompetitionMode;
}

interface EditFormData {
  competition_id: string;
  end_date: string;
  number_of_submissions: string;
  status: Status;
  visibility: Visibility;
  prize: Prize;
  leaderboard: Leaderboard;
  competition_mode: CompetitionMode;
}

interface UploadCompetitionAssetResponse {
  path_id: string;
  asset_type: 'logo' | 'readme' | 'evaluation';
  object_key: string;
  public_url: string;
}

const INITIAL_CREATE_DATA: CreateFormData = {
  path_id: '',
  title: '',
  description: '',
  submission: '',
  type: '',
  sponsored_by: '',
  start_date: '',
  end_date: '',
  number_of_submissions: '10',
  status: 'new',
  visibility: 'public',
  prize: 'empty',
  leaderboard: 'public',
  competition_mode: 'individual',
};

/** True when the create form differs from its blank starting values. */
const isCreateFormDirty = (data: CreateFormData): boolean => (
  (Object.keys(INITIAL_CREATE_DATA) as (keyof CreateFormData)[])
    .some((key) => data[key].trim() !== INITIAL_CREATE_DATA[key])
);

/** True when the edit form differs from the selected competition's saved values. */
const isEditFormDirty = (data: EditFormData, competition: Competition | null): boolean => {
  if (!competition) return false;
  const saved: Omit<EditFormData, 'competition_id'> = {
    end_date: competition.end_date?.slice(0, 10) || '',
    number_of_submissions: competition.number_of_submissions != null ? String(competition.number_of_submissions) : '',
    status: (competition.status as Status) || data.status,
    visibility: (competition.visibility as Visibility) || data.visibility,
    prize: (competition.prize as Prize) || data.prize,
    leaderboard: (competition.leaderboard as Leaderboard) || data.leaderboard,
    competition_mode: (competition.competition_mode as CompetitionMode) || data.competition_mode,
  };
  return (Object.keys(saved) as (keyof typeof saved)[]).some((key) => data[key] !== saved[key]);
};

const AdminCompetitionModal: React.FC<AdminCompetitionModalProps> = ({
  isOpen,
  mode,
  onClose,
  competitions,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const isCreate = mode === 'create';

  const [createData, setCreateData] = useState<CreateFormData>(INITIAL_CREATE_DATA);

  const [editData, setEditData] = useState<EditFormData>({
    competition_id: '',
    end_date: '',
    number_of_submissions: '',
    status: 'open',
    visibility: 'public',
    prize: 'empty',
    leaderboard: 'public',
    competition_mode: 'individual',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [readmeFile, setReadmeFile] = useState<File | null>(null);
  const [evaluationFile, setEvaluationFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setToastMessage('');
    if (isCreate) {
      setLogoFile(null);
      setReadmeFile(null);
      setEvaluationFile(null);
    }

    if (!isCreate) {
      // Default selection: first competition
      const first = competitions?.[0];
      if (first) {
        setEditData({
          competition_id: String(first.id),
          end_date: first.end_date?.slice(0, 10) || '',
          number_of_submissions: first.number_of_submissions != null ? String(first.number_of_submissions) : '',
          status: (first.status as Status) || 'open',
          visibility: (first.visibility as Visibility) || 'public',
          prize: ((first.prize as Prize) || 'empty'),
          leaderboard: (first.leaderboard as Leaderboard) || 'public',
          competition_mode: (first.competition_mode as CompetitionMode) || 'individual',
        });
      }
    }
  }, [isOpen, isCreate, competitions]);

  const selectedCompetition = useMemo(() => {
    if (isCreate) return null;
    const id = Number(editData.competition_id);
    return competitions.find((c) => c.id === id) || null;
  }, [competitions, editData.competition_id, isCreate]);

  useEffect(() => {
    if (!selectedCompetition) return;
    setEditData((prev) => ({
      ...prev,
      end_date: selectedCompetition.end_date?.slice(0, 10) || '',
      number_of_submissions: selectedCompetition.number_of_submissions != null ? String(selectedCompetition.number_of_submissions) : '',
      status: (selectedCompetition.status as Status) || prev.status,
      visibility: (selectedCompetition.visibility as Visibility) || prev.visibility,
      prize: ((selectedCompetition.prize as Prize) || prev.prize || 'empty'),
      leaderboard: (selectedCompetition.leaderboard as Leaderboard) || prev.leaderboard,
      competition_mode: (selectedCompetition.competition_mode as CompetitionMode) || prev.competition_mode,
    }));
  }, [selectedCompetition]);

  useEscapeToClose(onClose, {
    enabled: isOpen && !isLoading,
    confirmIfDirty: () => (isCreate
      ? isCreateFormDirty(createData) || Boolean(logoFile || readmeFile || evaluationFile)
      : isEditFormDirty(editData, selectedCompetition)),
  });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const uploadCompetitionAsset = async (
    pathId: string,
    assetType: 'logo' | 'readme' | 'evaluation',
    file: File
  ): Promise<UploadCompetitionAssetResponse> => {
    const formData = new FormData();
    formData.append('path_id', pathId);
    formData.append('asset_type', assetType);
    formData.append('file', file);

    const res = await apiService.upload('/utils/competitions/upload_asset', formData);
    return res.data as UploadCompetitionAssetResponse;
  };

  const requireExactFilename = (file: File | null, expected: string, fieldLabel: string): File => {
    if (!file) {
      throw new Error(`${fieldLabel} is required`);
    }
    if (file.name !== expected) {
      throw new Error(`${fieldLabel} must be exactly named ${expected}`);
    }
    return file;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const token = authUtils.getStoredToken();

      if (isCreate) {
        const pathId = createData.path_id.trim();
        if (!pathId) throw new Error('path_id is required');
        if (!createData.title.trim()) throw new Error('title is required');
        if (!createData.start_date) throw new Error('start_date is required');
        if (!createData.end_date) throw new Error('end_date is required');
        const logo = requireExactFilename(logoFile, 'logo.png', 'Logo file');
        const readme = requireExactFilename(readmeFile, 'Readme.md', 'Readme file');
        if (evaluationFile && evaluationFile.name !== 'Evaluation.md') {
          throw new Error('Evaluation file must be exactly named Evaluation.md');
        }

        await Promise.all([
          uploadCompetitionAsset(pathId, 'logo', logo),
          uploadCompetitionAsset(pathId, 'readme', readme),
        ]);

        if (evaluationFile) {
          await uploadCompetitionAsset(pathId, 'evaluation', evaluationFile);
        }

        await apiService.post('/competitions/create', {
          path_id: pathId,
          title: createData.title,
          description: createData.description || null,
          submission: createData.submission || null,
          type: createData.type || null,
          sponsored_by: createData.sponsored_by || null,
          start_date: createData.start_date,
          end_date: createData.end_date,
          number_of_submissions: Number(createData.number_of_submissions || '10'),
          status: createData.status,
          visibility: createData.visibility,
          prize: createData.prize,
          leaderboard: createData.leaderboard,
          competition_mode: createData.competition_mode,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setToastMessage('Competition created');
        onSuccess();
        onClose();
        return;
      }

      // edit mode
      const competitionId = Number(editData.competition_id);
      if (!competitionId) throw new Error('Select a competition to edit');
      if (!editData.end_date) throw new Error('end_date is required');

      await apiService.post(`/competitions/update/${competitionId}`, {
        end_date: editData.end_date,
        number_of_submissions: editData.number_of_submissions ? Number(editData.number_of_submissions) : null,
        status: editData.status,
        visibility: editData.visibility,
        prize: editData.prize,
        leaderboard: editData.leaderboard,
        competition_mode: editData.competition_mode,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToastMessage('Competition updated');
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Operation failed';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  const safeBackToAdmin = () => {
    // Ensure we close modal and navigate back to admin route without a full reload.
    onClose();
    navigate('/admin');
  };

  const handleManageCompetition = () => {
    const competitionId = Number(editData.competition_id);
    if (!competitionId) return;
    onClose();
    navigate(`/manage/${competitionId}`);
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className={`register-modal ${isCreate ? 'register-modal--admin-wide' : ''}`}
          role="dialog"
          aria-modal="true"
        >
          <div className="register-modal__header">
            <h2 className="register-modal__title">
              {isCreate ? 'Create Competition' : 'Edit Competition'}
            </h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={safeBackToAdmin}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            {isCreate ? (
              <div className="register-modal__fields">
                <div className="admin-pathid-row">
                  <Input
                    name="path_id"
                    label="path_id"
                    value={createData.path_id}
                    onChange={(e) => setCreateData((p) => ({ ...p, path_id: e.target.value }))}
                    required
                    helperText="  Id"
                  />

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={async () => {
                      try {
                        setError('');
                        const token = authUtils.getStoredToken();
                        const res = await apiService.post(
                          '/utils/generate_unique',
                          { length: 10 },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const unique = res?.data?.unique_string;
                        if (!unique) throw new Error('No unique_string returned');
                        setCreateData((p) => ({ ...p, path_id: unique }));
                      } catch (err: any) {
                        const msg = err?.response?.data?.detail || err?.message || 'Failed to generate path_id';
                        setError(String(msg));
                      }
                    }}
                  >
                    Generate
                  </Button>
                </div>
                <label className="input-wrapper">
                  <span className="input__label">logo.png *</span>
                  <input
                    className="input input--default input--md admin-file-input"
                    type="file"
                    accept=".png,image/png"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    required
                  />
                  <span className="input__helper">Required filename: logo.png</span>
                </label>
                <label className="input-wrapper">
                  <span className="input__label">Readme.md *</span>
                  <input
                    className="input input--default input--md admin-file-input"
                    type="file"
                    accept=".md,.markdown,text/markdown,text/plain"
                    onChange={(e) => setReadmeFile(e.target.files?.[0] || null)}
                    required
                  />
                  <span className="input__helper">Required filename: Readme.md</span>
                </label>
                <label className="input-wrapper">
                  <span className="input__label">Evaluation.md (optional)</span>
                  <input
                    className="input input--default input--md admin-file-input"
                    type="file"
                    accept=".md,.markdown,text/markdown,text/plain"
                    onChange={(e) => setEvaluationFile(e.target.files?.[0] || null)}
                  />
                  <span className="input__helper">If uploaded, filename must be Evaluation.md</span>
                </label>
                <Input
                  name="title"
                  label="Title"
                  value={createData.title}
                  onChange={(e) => setCreateData((p) => ({ ...p, title: e.target.value }))}
                  required
                />
                <Input
                  name="description"
                  label="Description"
                  value={createData.description}
                  onChange={(e) => setCreateData((p) => ({ ...p, description: e.target.value }))}
                />
                <Input
                  name="submission"
                  label="Submission"
                  value={createData.submission}
                  onChange={(e) => setCreateData((p) => ({ ...p, submission: e.target.value }))}
                />
                <label className="input-wrapper">
                  <span className="input__label">Type</span>
                  <select
                    className="input input--default input--md"
                    value={createData.type}
                    onChange={(e) => setCreateData((p) => ({ ...p, type: e.target.value }))}
                  >
                    <option value="">(select)</option>
                    <option value="csv">csv</option>
                    <option value="viz">viz</option>
                    <option value="video">video</option>
                    <option value="pdf">pdf</option>
                    <option value="code">code</option>
                    <option value="html">html</option>
                    <option value="github">github</option>
                    <option value="multi">multi</option>
                  </select>
                </label>
                <Input
                  name="sponsored_by"
                  label="Sponsored By"
                  value={createData.sponsored_by}
                  onChange={(e) => setCreateData((p) => ({ ...p, sponsored_by: e.target.value }))}
                />
                <Input
                  type="date"
                  name="start_date"
                  label="Start Date"
                  value={createData.start_date}
                  onChange={(e) => setCreateData((p) => ({ ...p, start_date: e.target.value }))}
                  required
                />
                <Input
                  type="date"
                  name="end_date"
                  label="End Date"
                  value={createData.end_date}
                  onChange={(e) => setCreateData((p) => ({ ...p, end_date: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  name="number_of_submissions"
                  label="Number of Submissions"
                  value={createData.number_of_submissions}
                  onChange={(e) => setCreateData((p) => ({ ...p, number_of_submissions: e.target.value }))}
                />

                <label className="input-wrapper">
                  <span className="input__label">Status</span>
                  <select
                    className="input input--default input--md"
                    value={createData.status}
                    onChange={(e) => setCreateData((p) => ({ ...p, status: e.target.value as Status }))}
                  >
                    <option value="new">new</option>
                    <option value="open">open</option>
                    <option value="closed">closed</option>
                    <option value="archived">archived</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Visibility</span>
                  <select
                    className="input input--default input--md"
                    value={createData.visibility}
                    onChange={(e) => setCreateData((p) => ({ ...p, visibility: e.target.value as Visibility }))}
                  >
                    <option value="public">public</option>
                    <option value="private">private</option>
                    <option value="unlisted">unlisted</option>
                    <option value="showcase">showcase</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Prize</span>
                  <select
                    className="input input--default input--md"
                    value={createData.prize}
                    onChange={(e) => setCreateData((p) => ({ ...p, prize: e.target.value as Prize }))}
                  >
                    <option value="empty">empty</option>
                    <option value="money">money</option>
                    <option value="badge">badge</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Leaderboard</span>
                  <select
                    className="input input--default input--md"
                    value={createData.leaderboard}
                    onChange={(e) => setCreateData((p) => ({ ...p, leaderboard: e.target.value as Leaderboard }))}
                  >
                    <option value="public">public</option>
                    <option value="private">private</option>
                    <option value="unlisted">unlisted</option>
                    <option value="final">final</option>
                  </select>
                </label>

                {config?.FEATURES?.TEAM_MODE_ENABLED && (
                  <label className="input-wrapper">
                    <span className="input__label">Competition Mode</span>
                    <select
                      className="input input--default input--md"
                      value={createData.competition_mode}
                      onChange={(e) => setCreateData((p) => ({ ...p, competition_mode: e.target.value as CompetitionMode }))}
                    >
                      <option value="individual">individual</option>
                      <option value="team">team</option>
                    </select>
                  </label>
                )}
              </div>
            ) : (
              <div className="register-modal__fields">
                <label className="input-wrapper">
                  <span className="input__label">Competition</span>
                  <select
                    className="input input--default input--md"
                    value={editData.competition_id}
                    onChange={(e) => setEditData((p) => ({ ...p, competition_id: e.target.value }))}
                  >
                    {competitions.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.title} (id={c.id})
                      </option>
                    ))}
                  </select>
                </label>

                <Input
                  type="date"
                  name="end_date"
                  label="End Date"
                  value={editData.end_date}
                  onChange={(e) => setEditData((p) => ({ ...p, end_date: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  name="number_of_submissions"
                  label="Number of Submissions"
                  value={editData.number_of_submissions}
                  onChange={(e) => setEditData((p) => ({ ...p, number_of_submissions: e.target.value }))}
                />

                <label className="input-wrapper">
                  <span className="input__label">Status</span>
                  <select
                    className="input input--default input--md"
                    value={editData.status}
                    onChange={(e) => setEditData((p) => ({ ...p, status: e.target.value as Status }))}
                  >
                    <option value="new">new</option>
                    <option value="open">open</option>
                    <option value="closed">closed</option>
                    <option value="archived">archived</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Visibility</span>
                  <select
                    className="input input--default input--md"
                    value={editData.visibility}
                    onChange={(e) => setEditData((p) => ({ ...p, visibility: e.target.value as Visibility }))}
                  >
                    <option value="public">public</option>
                    <option value="private">private</option>
                    <option value="unlisted">unlisted</option>
                    <option value="showcase">showcase</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Prize</span>
                  <select
                    className="input input--default input--md"
                    value={editData.prize}
                    onChange={(e) => setEditData((p) => ({ ...p, prize: e.target.value as Prize }))}
                  >
                    <option value="empty">empty</option>
                    <option value="money">money</option>
                    <option value="badge">badge</option>
                  </select>
                </label>

                <label className="input-wrapper">
                  <span className="input__label">Leaderboard</span>
                  <select
                    className="input input--default input--md"
                    value={editData.leaderboard}
                    onChange={(e) => setEditData((p) => ({ ...p, leaderboard: e.target.value as Leaderboard }))}
                  >
                    <option value="public">public</option>
                    <option value="private">private</option>
                    <option value="unlisted">unlisted</option>
                    <option value="final">final</option>
                  </select>
                </label>

                {config?.FEATURES?.TEAM_MODE_ENABLED && (
                  <label className="input-wrapper">
                    <span className="input__label">Competition Mode</span>
                    <select
                      className="input input--default input--md"
                      value={editData.competition_mode}
                      onChange={(e) => setEditData((p) => ({ ...p, competition_mode: e.target.value as CompetitionMode }))}
                    >
                      <option value="individual">individual</option>
                      <option value="team">team</option>
                    </select>
                  </label>
                )}
              </div>
            )}

            <div className="register-modal__actions">
              {!isCreate && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleManageCompetition}
                  disabled={isLoading || !editData.competition_id}
                >
                  Manage
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={safeBackToAdmin} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                {isCreate ? 'Create' : 'Update'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />
      )}
    </>
  );
};

export default AdminCompetitionModal;
